// src/pages/SettingsPage.tsx
import React from 'react';
import { useSearchParams } from 'react-router-dom';
import { Box } from '@mui/material';
import { styled } from '@mui/material/styles';
import SystemSettings from '../components/settings/SystemSettings';
import PetSettings, {
  PET_SUB_TAB_IDS,
  petSubTabIndexToId,
  type PetSubTabId,
} from '../components/settings/PetSettings';
import KennelsSettings from '../components/settings/KennelSettings';
import VetsSettings from '../components/settings/VetsSettings';
import UsersSettings from '../components/settings/UserSettings';
import RecycleBin from '../components/settings/RecycleBin';
import PageTitle from '../components/common/PageTitle';
import { SettingsPageSubMenu } from '../components/menus';
import { allTabItems } from '../components/menus/SettingsPageSubMenu';
import { usePermissions } from '../hooks/usePermissions';
import { useTabQueryParam } from '../hooks/useTabQueryParam';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => (
  <div
    role="tabpanel"
    hidden={value !== index}
    id={`settings-tabpanel-${index}`}
    aria-labelledby={`settings-tab-${index}`}
  >
    {value === index && <Box>{children}</Box>}
  </div>
);

const MainContainer = styled(Box)({ position: 'relative' });

const PageContainer = styled(Box)(({ theme }) => ({
  margin: '0 auto',
  maxWidth: '100%',
  padding: '0',
  [theme.breakpoints.down('sm')]: { padding: '0' },
}));

const PETS_IDX = 2;
const PET_PRICES_SUB_IDX = 4;
const PRICES_SHORTCUT_IDX = 1;
const PRICES_SUB_TAB_ID: PetSubTabId = petSubTabIndexToId(PET_PRICES_SUB_IDX);

// `allTabItems` (from SettingsPageSubMenu) is the single source of truth for
// which top-level tabs exist and what their ids are - reused here rather
// than a second, driftable list. "prices" is deliberately excluded: it's a
// display-only shortcut button, never a real persisted tab of its own (see
// `effectiveTab` below).
const REAL_TAB_IDS = allTabItems
  .filter((item) => item.id !== 'prices')
  .map((item) => item.id);
const REAL_TAB_INDEX_BY_ID: Record<string, number> = Object.fromEntries(
  allTabItems.map((item) => [item.id, item.index])
);
const REAL_TAB_ID_BY_INDEX: Record<number, string> = Object.fromEntries(
  allTabItems.map((item) => [item.index, item.id])
);

const SettingsPage: React.FC = () => {
  // Reading still goes through the shared hook (id validation/fallback,
  // stays in sync with PetSettings' own petSubTab hook via the router's
  // shared location state). Writing does NOT go through the hooks' own
  // setters here: this handler needs to update `tab` and `petSubTab` (and
  // clear `recycleSubTab`) together, and react-router's setSearchParams
  // doesn't merge two calls made back-to-back in the same handler - each
  // one builds its next URLSearchParams from the same pre-update snapshot,
  // so the second call silently clobbers the first (see the "Caution"
  // note on useTabQueryParam). A single combined write below avoids that
  // entirely.
  const [tabId] = useTabQueryParam('tab', REAL_TAB_IDS, 'system');
  const [petSubTabId] = useTabQueryParam<PetSubTabId>(
    'petSubTab',
    PET_SUB_TAB_IDS,
    'types'
  );
  const [searchParams, setSearchParams] = useSearchParams();
  const { canManageUsers, canManageSettings } = usePermissions();

  const selectedTab = REAL_TAB_INDEX_BY_ID[tabId];

  const handleTabChange = (tab: number) => {
    const next = new URLSearchParams(searchParams);
    if (tab === PRICES_SHORTCUT_IDX) {
      next.set('tab', 'pets');
      next.set('petSubTab', PRICES_SUB_TAB_ID);
    } else {
      const id = REAL_TAB_ID_BY_INDEX[tab];
      if (id === 'system') {
        next.delete('tab');
      } else {
        next.set('tab', id);
      }
      next.delete('petSubTab');
    }
    // Whatever tab we're headed to, a sub-tab left over from a previously
    // visited section (Recycle Bin's own `recycleSubTab`) is no longer
    // relevant - drop it rather than let the URL accumulate stale params
    // from earlier in the session. (Landing back on Recycle Bin always
    // resets to its Clients tab this way too, which matches its prior
    // behavior of remounting fresh every time it's navigated to.)
    next.delete('recycleSubTab');
    setSearchParams(next, { replace: true });
  };

  // Only meaningful once the PRICES shortcut has actually been used to get
  // here; for any other route to the Pets tab (direct click, deep link,
  // refresh), forceTab stays undefined and PetSettings restores its own
  // sub-tab independently from the same URL param.
  const petSubTab =
    selectedTab === PETS_IDX && petSubTabId === PRICES_SUB_TAB_ID
      ? PET_PRICES_SUB_IDX
      : undefined;

  const effectiveTab =
    selectedTab === PETS_IDX && petSubTab === PET_PRICES_SUB_IDX
      ? PRICES_SHORTCUT_IDX
      : selectedTab;

  const isPetsTab = selectedTab === PETS_IDX;

  return (
    <MainContainer>
      <PageTitle title="Settings" showSeparator={false} modeText="" />
      <PageContainer>
        <SettingsPageSubMenu
          selectedTab={effectiveTab}
          setSelectedTab={handleTabChange}
          bottomBorder={!isPetsTab}
          buttonBottomBorder={isPetsTab}
          showUsersTab={canManageUsers}
          showRecycleBinTab={canManageSettings}
        />
        <Box>
          <TabPanel value={selectedTab} index={0}>
            <SystemSettings />
          </TabPanel>
          <TabPanel value={selectedTab} index={2}>
            <PetSettings forceTab={petSubTab} />
          </TabPanel>
          <TabPanel value={selectedTab} index={3}>
            <KennelsSettings />
          </TabPanel>
          <TabPanel value={selectedTab} index={4}>
            <VetsSettings />
          </TabPanel>
          {canManageUsers && (
            <TabPanel value={selectedTab} index={5}>
              <UsersSettings />
            </TabPanel>
          )}
          {canManageSettings && (
            <TabPanel value={selectedTab} index={6}>
              <RecycleBin />
            </TabPanel>
          )}
        </Box>
      </PageContainer>
    </MainContainer>
  );
};

export default SettingsPage;
